import React from "react";
import { Box, IconButton, InputAdornment, Stack, Typography } from "@mui/material";
import { Info } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import { checkUserDetailsFromCookie } from "../../../../api_calls/userManagement";
import NumericInputField from "../../../../components/commonComponents/TextInputs/NumericInputField";
import TextInputField from "../../../../components/commonComponents/TextInputs/TextInputField";
import SingleSelect from "../../../../components/commonComponents/Selectors/SingleSelect";
import {
  HtmlTooltip,
  StyledBlackInformationTypography,
  StyledDarkRedInformationTypography,
} from "../../../../components/commonComponents/StyledComponents/styled";

const StoreInfraInfo = ({ fieldErrors, usedFor }) => {
  const userStateDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();

  const handleFieldChange = (storeLocData) => {
    const existing =
      typeof userStateDetails.new_store_loc_data !== "undefined" ? userStateDetails.new_store_loc_data : {};
    dispatchAction(updateStateData({ new_store_loc_data: { ...existing, ...storeLocData } }));
  };

  const fieldErr = (key) =>
    typeof fieldErrors !== "undefined" && typeof fieldErrors[key] !== "undefined" && fieldErrors[key] !== ""
      ? fieldErrors[key]
      : "";

  return (
    <>
      <Box height={20} />

      {/* Read-only store identifiers shown in update mode */}
      {usedFor === "update" && (
        <>
          <Box>
            <Stack direction={"row"} spacing={3}>
              <Box>
                <Stack direction={"row"} spacing={1}>
                  <StyledDarkRedInformationTypography variant="body2">Cyreen Store ID :</StyledDarkRedInformationTypography>
                  <StyledBlackInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                    <strong>{userStateDetails?.store_overview?.number_store_cyreen ?? "__"}</strong>
                  </StyledBlackInformationTypography>
                </Stack>
              </Box>
              <Box>
                <Stack direction={"row"} spacing={1}>
                  <StyledDarkRedInformationTypography variant="body2">Store IP Address :</StyledDarkRedInformationTypography>
                  <StyledBlackInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                    {userStateDetails?.db_store_ip ? (
                      <strong>
                        10.{userStateDetails.db_store_ip.second_octet}.{userStateDetails.db_store_ip.third_octet}.0/24
                      </strong>
                    ) : (
                      <strong>__</strong>
                    )}
                  </StyledBlackInformationTypography>
                </Stack>
              </Box>
              <Box>
                <Stack direction={"row"} spacing={1}>
                  <StyledDarkRedInformationTypography variant="body2">Data Processing :</StyledDarkRedInformationTypography>
                  <StyledBlackInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                    <strong>{userStateDetails?.store_overview?.store_status === 1 ? "Active" : "Inactive"}</strong>
                  </StyledBlackInformationTypography>
                </Stack>
              </Box>
            </Stack>
          </Box>
          <Box height={30} />
        </>
      )}

      <Stack direction={"row"} spacing={4}>
        <NumericInputField
          fieldRequired={false}
          elemId="retailer_store_id"
          label={"Retailer Store ID"}
          fieldName="retailer_store_id"
          errorStyle="text"
          defaultVal={userStateDetails?.new_store_loc_data?.retailer_store_id ?? ""}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ retailer_store_id: e.target.value });
          }}
          fieldError={fieldErr("retailer_store_id")}
          fieldInfo={{
            endAdornment: (
              <InputAdornment position="end">
                <HtmlTooltip
                  title={
                    <Box width={250}>
                      <Typography variant="body2">
                        <strong>Retailer Store ID:</strong> Refers to the internal store ID assigned by the retailer
                        and is included in the receipt data. The Retailer Store ID is essential for processing this
                        store's receipt data.
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton>
                    <Info />
                  </IconButton>
                </HtmlTooltip>
              </InputAdornment>
            ),
          }}
        />
        <SingleSelect
          enableFullWidth={true}
          disableSearch={true}
          label="Checkout Tracking*"
          elemId={"checkoutTrackingStore"}
          readOnlyFlag={false}
          options={["Automated", "Manual/No"].map((v) => ({ label: v, value: v }))}
          defaultVal={
            typeof userStateDetails?.new_store_loc_data?.automated_checkout !== "undefined"
              ? {
                label: userStateDetails.new_store_loc_data.automated_checkout,
                value: userStateDetails.new_store_loc_data.automated_checkout,
              }
              : { label: "Automated", value: "Automated" }
          }
          selectionCheck={(option, value) => option.value === value.value}
          onChangeCall={(selectedOption) => {
            handleFieldChange({ automated_checkout: selectedOption.value });
          }}
          fieldError={fieldErr("automated_checkout")}
          fieldInfo={
            <InputAdornment position="end" sx={{ marginRight: "15px" }}>
              <HtmlTooltip
                title={
                  <Box width={250}>
                    <Typography variant="body2">
                      <strong>Automated:</strong> Shoppers are automatically recorded at the checkouts using C.A.P.
                      tracking technology. <br />
                      <strong>Manual/No:</strong> Shoppers are manually recorded at the checkouts by the store's
                      personnel or are not recorded at all.
                    </Typography>
                  </Box>
                }
              >
                <IconButton>
                  <Info />
                </IconButton>
              </HtmlTooltip>
            </InputAdornment>
          }
        />
      </Stack>

      <Box height={47} />
      <Stack direction={"row"} spacing={4}>
        <NumericInputField
          fieldRequired={false}
          elemId="equipped_trolleys"
          label="Equipped Trolleys"
          fieldName="equipped_trolleys"
          errorStyle="text"
          defaultVal={userStateDetails?.new_store_loc_data?.store_trolleys ?? ""}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ store_trolleys: e.target.value });
          }}
          fieldError={fieldErr("store_trolleys")}
        />
        <NumericInputField
          fieldRequired={false}
          elemId="equipped_baskets"
          label="Equipped Baskets"
          fieldName="equipped_baskets"
          errorStyle="text"
          defaultVal={userStateDetails?.new_store_loc_data?.store_baskets ?? ""}
          onChangeCall={(e) => {
            e.preventDefault();
            handleFieldChange({ store_baskets: e.target.value });
          }}
          fieldError={fieldErr("store_baskets")}
        />
      </Stack>

      <Box height={30} />

      <Stack direction={"row"} spacing={4}>
        <Box flex={1}>
          <TextInputField
            fieldRequired={false}
            elemId="store_size"
            label="Store Size (in m²)"
            fieldName="store_size"
            showlabel={true}
            defaultVal={userStateDetails?.new_store_loc_data?.store_size ?? ""}
            inputType={"text"}
            onChangeCall={(e) => {
              e.preventDefault();
              handleFieldChange({ store_size: e.target.value });
            }}
            fieldError={fieldErr("store_size")}
          />
        </Box>

        <Box flex={1} />
      </Stack>
    </>
  );
};

export default StoreInfraInfo;
